<template>
  <div class="app-container">
    <!-- Search Area -->
    <el-form
      :inline="true"
      :model="searchForm"
      class="demo-form-inline"
      label-position="top"
      size="small"
    >
      <el-row :gutter="10">
        <el-col :span="4">
          <el-form-item :label="$t('CenterForSorting.machineName')">
            <el-input
              v-model="searchForm.machineName"
              :placeholder="$t('CenterForSorting.pleaseInput')"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item :label="$t('CenterForSorting.machineCode')">
            <el-input
              v-model="searchForm.machineCode"
              :placeholder="$t('CenterForSorting.pleaseInput')"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item :label="$t('CenterForSorting.transferWarehouse')">
            <el-select
              v-model="searchForm.transferWarehouseId"
              :placeholder="$t('CenterForSorting.pleaseChoose')"
              clearable
              filterable
              @change="handleSearch"
            >
              <el-option
                v-for="item in transitOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="5">
          <el-form-item :label="$t('CenterForSorting.State')">
            <el-radio-group v-model="searchForm.status" size="mini" @change="handleSearch">
              <el-radio-button :label="null">{{ $t('CenterForSorting.all') }}</el-radio-button>
              <el-radio-button :label="1">{{ $t('CenterForSorting.enable') }}</el-radio-button>
              <el-radio-button :label="0">{{ $t('CenterForSorting.disable') }}</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="5">
          <div style="margin-top: 28px">
            <el-button
              icon="el-icon-search" size="mini" type="primary"
              @click="handleSearch"
            >{{ $t('CenterForSorting.search') }}
            </el-button>
            <el-button icon="el-icon-refresh" size="mini" @click="resetSearch">{{ $t('CenterForSorting.reset') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </el-form>

    <div style="display: flex; justify-content: flex-end; margin: 10px 0">
      <el-button
        v-permit:remove="'btn:sorting:machineList:add'"
        icon="el-icon-plus"
        size="small"
        type="primary"
        @click="handleAdd"
      >{{ $t('CenterForSorting.newMachine') }}
      </el-button>
    </div>

    <!-- Table Area -->
    <el-card>
      <el-table
        v-loading="listLoading"
        :data="tableData"
        border
        fit
        highlight-current-row
        style="width: 100%"
      >
        <el-table-column
          :label="$t('CenterForSorting.ind')"
          align="center"
          type="index"
          width="60"
        />
        <el-table-column
          :label="$t('CenterForSorting.machineName')"
          align="center"
          min-width="120"
          prop="machineName"
        />
        <el-table-column
          :label="$t('CenterForSorting.machineCode')"
          align="center"
          min-width="120"
          prop="machineCode"
        />
        <el-table-column
          :label="$t('CenterForSorting.transferWarehouse')"
          align="center"
          min-width="120"
          prop="transferWarehouseName"
        />
        <el-table-column
          :label="$t('CenterForSorting.chuteCount')"
          align="center"
          prop="chuteCount"
          width="100"
        />
        <el-table-column
          :label="$t('CenterForSorting.usingRuleGroup')"
          align="center"
          min-width="120"
          prop="currentRuleGroupName"
        />
        <el-table-column
          :label="$t('scripting.manualFeedTable')"
          align="center"
          min-width="120"
          prop="manualSupplyTableCount"
        />
        <el-table-column
          :label="$t('scripting.automaticFeedTable')"
          align="center"
          min-width="120"
          prop="autoSupplyTableCount"
        />
        <el-table-column :label="$t('CenterForSorting.deviceStatus')" align="center" width="100">
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.status"
              :active-value="1"
              :disabled="isOperationStatus"
              :inactive-value="0"
              @change="handleStatusChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('CenterForSorting.operatingStatus')" align="center" width="100">
          <template slot-scope="{ row }">
            <el-tag
              :type="
                row.operatingStatus === 1
                  ? 'success'
                  : row.operatingStatus === 0
                    ? 'info'
                    : 'warning'
              "
            >
              {{
                row.operatingStatus === 1
                  ? $t('CenterForSorting.running')
                  : row.operatingStatus === 0
                    ? $t('CenterForSorting.shutdown')
                    : $t('CenterForSorting.unknown')
              }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('CenterForSorting.operation')"
          align="center"
          fixed="right"
          width="250"
        >
          <template slot-scope="{ row }">
            <el-button
              v-if="isOperationBtn"
              icon="el-icon-view"
              size="mini"
              type="text"
              @click="handleChuteView(row)"
            >{{ $t('CenterForSorting.chuteView') }}
            </el-button>
            <el-button
              v-permit:remove="'btn:sorting:machineList:Edit'"
              icon="el-icon-edit"
              size="mini"
              type="text"
              @click="handleEdit(row)"
            >{{ $t('CenterForSorting.edit') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div style="margin-top: 20px; text-align: right">
        <el-pagination
          :current-page.sync="listQuery.pageNumber"
          :page-size.sync="listQuery.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="fetchData"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog
      :close-on-click-modal="false"
      :title="textMap[dialogStatus]"
      :visible.sync="dialogFormVisible"
      top="2vh"
      width="600px"
    >
      <el-form
        ref="dataForm"
        :model="temp"
        :rules="rules"
        class="compact-form"
        label-position="left"
      >
        <el-form-item :label="$t('CenterForSorting.machineName')" prop="machineName">
          <el-input v-model="temp.machineName" :placeholder="$t('CenterForSorting.pleaseInput')" />
        </el-form-item>
        <el-form-item
          v-if="dialogStatus === 'create'"
          :label="$t('CenterForSorting.machineCode')"
          prop="machineCode"
        >
          <el-input v-model="temp.machineCode" :placeholder="$t('CenterForSorting.pleaseInput')" />
        </el-form-item>
        <el-form-item v-else :label="$t('CenterForSorting.machineCode')">
          <el-input v-model="temp.machineCode" disabled />
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.authSecretKey')" prop="authSecretKey">
          <el-input
            v-model="temp.authSecretKey"
            :placeholder="$t('CenterForSorting.pleaseInput')"
          />
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.transferWarehouse')" prop="transferWarehouseId">
          <el-select
            v-model="temp.transferWarehouseId"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            filterable
            style="width: 100%"
            @change="handleWarehouseChange"
          >
            <el-option
              v-for="item in transitOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.operateUser')" prop="operateUserId">
          <el-select
            v-model="temp.operateUserId"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="item in userList"
              :key="item.id"
              :label="item.username"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="dialogStatus === 'create'"
          :label="$t('CenterForSorting.chuteNum')"
          prop="chuteCount"
        >
          <el-input-number
            v-model="temp.chuteCount"
            :label="$t('CenterForSorting.chuteNum')"
            :max="999"
            :min="1"
          />
        </el-form-item>
        <el-form-item v-else :label="$t('CenterForSorting.chuteNum')" prop="chuteCount">
          <el-input-number
            v-model="temp.chuteCount"
            :label="$t('CenterForSorting.chuteNum')"
            :max="999"
            :min="1"
          />
          <div class="tip-text" style="color: #999; font-size: 12px">
            {{ $t('CenterForSorting.chuteCountTip') }}
          </div>
        </el-form-item>
        <!--  人工供包      -->
        <el-form-item :label="$t('scripting.manualFeedTable')" prop="manualSupplyTableCount">
          <el-input-number
            v-model="temp.manualSupplyTableCount"
            :label="$t('CenterForSorting.chuteNum')"
            :max="99"
            :min="0"
          />
        </el-form-item>
        <!--  自动供包      -->
        <el-form-item :label="$t('scripting.automaticFeedTable')" prop="autoSupplyTableCount">
          <el-input-number
            v-model="temp.autoSupplyTableCount"
            :label="$t('CenterForSorting.chuteNum')"
            :max="99"
            :min="0"
          />
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.scanRegex')" prop="scanNumberRegex">
          <div style="display: flex; align-items: flex-start">
            <el-input
              v-model="temp.scanNumberRegex"
              :rows="3"
              placeholder="^WB\d{11}$"
              style="flex: 1"
              type="textarea"
            />
            <el-popover
              v-model="regexTestVisible" placement="right" trigger="click"
              width="300"
            >
              <div>
                <el-input
                  v-model="testRegexString"
                  :placeholder="$t('CenterForSorting.inputTestString')"
                  size="small"
                  @input="handleTestRegex"
                >
                  <template slot="append">
                    <i
                      v-if="testRegexString && regexTestResult !== null"
                      :class="regexTestResult ? 'el-icon-check' : 'el-icon-close'"
                      :style="{ color: regexTestResult ? 'green' : 'red' }"
                    />
                  </template>
                </el-input>
                <div
                  v-if="testRegexString && regexTestResult !== null"
                  style="margin-top: 5px; font-size: 12px"
                >
                  <span v-if="regexTestResult" style="color: green">{{
                    $t('CenterForSorting.regexPass')
                  }}</span>
                  <span v-else style="color: red">{{ $t('CenterForSorting.regexFail') }}</span>
                </div>
              </div>
              <el-button
                slot="reference"
                :title="$t('CenterForSorting.testRegex')"
                icon="el-icon-aim"
                style="margin-left: 10px"
                type="text"
              />
            </el-popover>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFormVisible = false">{{ $t('CenterForSorting.cancel') }}
        </el-button>
        <el-button
          :loading="dialogBtnLoading"
          type="primary"
          @click="dialogStatus === 'create' ? createData() : updateData()"
        >
          {{ $t('CenterForSorting.confirm') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  addMachine,
  getMachineList,
  getMachineUsers,
  updateChuteCount,
  updateMachine,
  updateMachineStatus
} from '@/api/sorting/machine';
import { apiSelectTransitCenter } from '@/api/automaticSorting';

export default {
  name: 'MachineList',
  data() {
    return {
      tableData: [],
      total: 0,
      listLoading: true,
      listQuery: {
        pageNumber: 1,
        pageSize: 10
      },
      searchForm: {
        machineName: '',
        machineCode: '',
        status: null,
        transferWarehouseId: ''
      },
      transitOptions: [],
      userList: [],
      dialogFormVisible: false,
      dialogStatus: '',
      textMap: {
        update: this.$t('CenterForSorting.editMachine'),
        create: this.$t('CenterForSorting.newMachine')
      },
      temp: {
        id: undefined,
        machineName: '',
        machineCode: '',
        authSecretKey: '',
        transferWarehouseId: '',
        operateUserId: '',
        chuteCount: 50,
        scanNumberRegex: '',
        manualSupplyTableCount: 0,
        autoSupplyTableCount: 0
      },
      originalChuteCount: 0, // 存储原始格口数量以检测更改
      dialogBtnLoading: false,
      regexTestVisible: false,
      testRegexString: '',
      regexTestResult: null,
      rules: {
        machineName: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        machineCode: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        authSecretKey: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        transferWarehouseId: [
          { required: true, message: this.$t('CenterForSorting.pleaseChoose'), trigger: 'change' }
        ],
        operateUserId: [
          { required: true, message: this.$t('CenterForSorting.pleaseChoose'), trigger: 'change' }
        ],
        chuteCount: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        scanNumberRegex: [
          {
            validator: (rule, value, callback) => {
              if (!value) {
                callback();
              } else {
                try {
                  new RegExp(value);
                  callback();
                } catch (e) {
                  callback(new Error(this.$t('CenterForSorting.invalidRegex')));
                }
              }
            },
            trigger: 'blur'
          }
        ],
        manualSupplyTableCount: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        autoSupplyTableCount: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ]
      },
      isOperationBtn: false,
      isOperationStatus: true
    };
  },
  created() {
    if (this.$store.getters.button_permissions.includes('btn:sorting:machineList:status')) {
      this.isOperationStatus = false;
    } else {
      this.isOperationStatus = true;
    }
    if (this.$store.state.user.roles.includes('menu:sorting:chuteView')) {
      this.isOperationBtn = true;
    } else {
      this.isOperationBtn = false;
    }
    this.getTransitCenterList();
    this.fetchData();
  },
  methods: {
    getTransitCenterList() {
      apiSelectTransitCenter().then((response) => {
        if (response.code === 200) {
          this.transitOptions = response.data;
        }
      });
    },
    fetchData() {
      this.listLoading = true;
      const params = {
        pageNumber: this.listQuery.pageNumber,
        pageSize: this.listQuery.pageSize,
        params: {
          machineName: this.searchForm.machineName || undefined,
          machineCode: this.searchForm.machineCode || undefined,
          transferWarehouseId: this.searchForm.transferWarehouseId || undefined,
          status: this.searchForm.status
        }
      };
      getMachineList(params)
        .then((response) => {
          this.tableData = response.data.records;
          this.total = response.data.total;
          this.listLoading = false;
        })
        .catch(() => {
          this.listLoading = false;
        });
    },
    handleSearch() {
      this.listQuery.pageNumber = 1;
      this.fetchData();
    },
    resetSearch() {
      this.searchForm = {
        machineName: '',
        machineCode: '',
        status: null,
        transferWarehouseId: ''
      };
      this.handleSearch();
    },
    handleSizeChange(val) {
      this.listQuery.pageSize = val;
      this.fetchData();
    },
    resetTemp() {
      this.temp = {
        id: undefined,
        machineName: '',
        machineCode: '',
        authSecretKey: '',
        transferWarehouseId: '',
        operateUserId: '',
        chuteCount: 50,
        scanNumberRegex: '',
        manualSupplyTableCount: 0,
        autoSupplyTableCount: 0
      };
      this.originalChuteCount = 0;
      this.userList = [];
      this.regexTestVisible = false;
      this.testRegexString = '';
      this.regexTestResult = null;
    },
    handleTestRegex() {
      if (!this.temp.scanNumberRegex) {
        this.regexTestResult = null;
        return;
      }
      if (!this.testRegexString) {
        this.regexTestResult = null;
        return;
      }
      try {
        const regex = new RegExp(this.temp.scanNumberRegex);
        this.regexTestResult = regex.test(this.testRegexString);
      } catch (e) {
        this.regexTestResult = false;
      }
    },
    getMachineUsersList(siteId) {
      if (!siteId) {
        this.userList = [];
        return;
      }
      getMachineUsers({ siteId }).then((response) => {
        if (response.code === 200) {
          this.userList = response.data;
        }
      });
    },
    handleWarehouseChange(val) {
      this.temp.operateUserId = '';
      this.getMachineUsersList(val);
    },
    handleAdd() {
      this.resetTemp();
      this.dialogStatus = 'create';
      this.dialogFormVisible = true;
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    createData() {
      this.$refs['dataForm'].validate((valid) => {
        if (valid) {
          this.dialogBtnLoading = true;
          addMachine(this.temp)
            .then(() => {
              this.dialogFormVisible = false;
              this.dialogBtnLoading = false;
              this.$notify({
                title: this.$t('CenterForSorting.success'),
                message: this.$t('CenterForSorting.createSuccess'),
                type: 'success',
                duration: 2000
              });
              this.fetchData();
            })
            .catch(() => {
              this.dialogBtnLoading = false;
            });
        }
      });
    },
    handleEdit(row) {
      this.temp = Object.assign({}, row); // 复制对象
      this.originalChuteCount = row.chuteCount;
      this.dialogStatus = 'update';
      this.dialogFormVisible = true;
      if (this.temp.transferWarehouseId) {
        this.getMachineUsersList(this.temp.transferWarehouseId);
      }
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    async updateData() {
      this.$refs['dataForm'].validate(async(valid) => {
        if (valid) {
          this.dialogBtnLoading = true;
          try {
            // 先更新基本信息
            await updateMachine(this.temp);

            // 检查格口数量是否变更
            if (this.temp.chuteCount !== this.originalChuteCount) {
              try {
                await updateChuteCount({
                  machineId: this.temp.id,
                  chuteCount: this.temp.chuteCount
                });
              } catch (error) {
                console.error('更新格口数量失败', error);
                this.$message.warning(this.$t('CenterForSorting.updateChuteFailTip'));
                this.dialogFormVisible = false;
                this.fetchData();
                return; // 提前退出
              }
            }

            this.dialogFormVisible = false;
            this.$notify({
              title: this.$t('CenterForSorting.success'),
              message: this.$t('CenterForSorting.updateSuccess'),
              type: 'success',
              duration: 2000
            });
            this.fetchData();
          } catch (error) {
            console.error(error);
          } finally {
            this.dialogBtnLoading = false;
          }
        }
      });
    },
    handleStatusChange(row) {
      const statusText =
        row.status === 1 ? this.$t('CenterForSorting.enable') : this.$t('CenterForSorting.disable');
      this.$confirm(
        this.$t('CenterForSorting.confirmStatusChange', { status: statusText }),
        this.$t('CenterForSorting.tips'),
        {
          confirmButtonText: this.$t('CenterForSorting.sure'),
          cancelButtonText: this.$t('CenterForSorting.cancel'),
          type: 'warning'
        }
      )
        .then(() => {
          updateMachineStatus({ id: row.id, status: row.status })
            .then(() => {
              this.$message({
                type: 'success',
                message: this.$t('CenterForSorting.operateSuccess')
              });
              this.fetchData();
            })
            .catch(() => {
              row.status = row.status === 1 ? 0 : 1; // 发生错误时恢复
            });
        })
        .catch(() => {
          row.status = row.status === 1 ? 0 : 1; // 取消时恢复
        });
    },
    handleChuteView(row) {
      this.$router.push({
        path: '/expressDataCenter/sortingManagementNew/chuteView',
        query: { machineId: row.id }
      });
    }
  }
};
</script>

<style scoped>
.filter-container {
  padding-bottom: 10px;
}

/* 给 el-form 加个类，用于穿透修改样式 */
.compact-form >>> .el-form-item {
  margin-bottom: 8px !important; /* 上下间距 10px */
}
</style>
